// brain-facts.ts — the request bodies behind the entity page's three admin
// edits (POST /api/admin/brain/corrections): INVALIDATE a claim, ADD a fact
// (a claim, or a relation to another entity), REPLACE a fact (supersede: the
// old claim invalidated and the new one added in ONE submission). The server
// and the door decide; these only shape what the dialog collected and name a
// missing field before anything is sent. Exported so brain-facts.test.mjs can
// pin them without a DOM.

export type FactOp = 'invalidate' | 'add' | 'supersede';
export interface FactInput { property?: string; value?: string; to?: string; relation?: boolean; valid_from?: string; note?: string }
export type FactRequest = { ok: true; body: Record<string, unknown> } | { ok: false; error: string };

/** A typed value: `true`/`false` and plain numbers are sent as such, anything
 *  else as the text typed (the ledger's scalar grammar). */
export function typedValue(raw: string): string | number | boolean {
  const t = raw.trim();
  if (t === 'true') return true;
  if (t === 'false') return false;
  if (/^-?\d+(\.\d+)?$/.test(t)) return Number(t);
  return t;
}

export function factRequest(op: FactOp, entity: string, claim: string | null, input: FactInput): FactRequest {
  const note = (input.note ?? '').trim();
  const property = (input.property ?? '').trim();
  const value = (input.value ?? '').trim();
  const to = (input.to ?? '').trim();
  const vf = (input.valid_from ?? '').trim();
  const extra: Record<string, unknown> = { ...(vf ? { valid_from: vf } : {}), note };
  if (op === 'invalidate') return claim ? { ok: true, body: { op, entity, claim, note } } : { ok: false, error: 'pick the claim to invalidate' };
  if (op === 'supersede') {
    if (!claim) return { ok: false, error: 'pick the claim to replace' };
    if (!!value === !!to) return { ok: false, error: 'give the new value, or the new target entity id — exactly one' };
    return { ok: true, body: { op, entity, claim, ...(to ? { to } : { value: typedValue(value) }), ...extra } };
  }
  if (!property) return { ok: false, error: 'property is required' };
  if (input.relation) return to ? { ok: true, body: { op, entity, relation: true, property, to, ...extra } } : { ok: false, error: 'a relation needs the target entity id' };
  if (!!value === !!to) return { ok: false, error: 'give a value, or a target entity id — exactly one' };
  return { ok: true, body: { op, entity, property, ...(to ? { to } : { value: typedValue(value) }), ...extra } };
}

/** What the page shows for an answer that is not a landing: the route's own
 *  error, or the door's runLine (which names the refusing gate), verbatim. */
export function factRefusal(status: number, d: { error?: string; runLine?: string; refused_by?: string | null } | null): string {
  if (d?.error) return `${status} ${d.error}`;
  if (d?.runLine) return d.runLine;
  if (d?.refused_by) return `${status} ${d.refused_by}`;
  return `correction ${status}`;
}
